<template>
  <div class="tw-text-left">
    <div v-if="$apollo.queries.order.loading" class="tw-flex tw-items-center tw-gap-2 tw-py-6">
      <font-awesome-icon icon="circle-notch" spin class="tw-h-4 tw-w-4 tw-text-slate-500" />
      <span class="tw-text-sm tw-text-slate-600">Loading order…</span>
    </div>

    <div v-if="order" class="tw-space-y-4">
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-items-stretch">
        <div class="tw-h-full tw-flex tw-flex-col dispatch-order-card__address">
          <TerminalAddressCard :order="order" :isExportOrder="isExportOrder"></TerminalAddressCard>
        </div>
        <div class="tw-h-full tw-flex tw-flex-col dispatch-order-card__address">
          <ReceiverAddressCard @refreshOrder="refreshOrder" :order="order"></ReceiverAddressCard>
        </div>
        <div class="tw-h-full tw-flex tw-flex-col dispatch-order-card__address">
          <BillToAddressCard
            :billingContact="order.billingContact"
            :orderId="order.id"
            :billTo="order.billTo"
          ></BillToAddressCard>
        </div>
      </div>

      <div class="tw-overflow-x-auto">
          <table class="tw-min-w-full tw-text-left tw-text-sm tw-text-slate-700">
            <thead class="tw-bg-slate-50/70 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
              <tr>
                <th class="tw-px-4 tw-py-3">Ship Line</th>
                <th class="tw-px-4 tw-py-3" v-if="isExportOrder">Booking number</th>
                <th class="tw-px-4 tw-py-3" v-else-if="order.billOfLading">Bill Of Lading</th>
                <th class="tw-px-4 tw-py-3">Vessel Name</th>
                <th class="tw-px-4 tw-py-3">{{ !isExportOrder ? 'Vessel ETA' : 'Vessel Cutoff' }}</th>
                <th class="tw-px-4 tw-py-3">Reference #</th>
                <th class="tw-px-4 tw-py-3">Billing Customer Ref #</th>
                <th class="tw-px-4 tw-py-3"></th>
              </tr>
            </thead>
            <tbody class="tw-divide-y tw-divide-slate-200/70">
              <tr class="odd:tw-bg-white/30">
                <td class="tw-px-4 tw-py-3">
                  <span v-if="!isEditingVesselInformation">{{ order.shipLine }}</span>
                  <FilterableDropdown
                    v-else
                    taggable
                    push-tag
                    v-model="shipLine"
                    :options="steamshipLines"
                    :components="{ OpenIndicator: null, Deselect: null }"
                  ></FilterableDropdown>
                </td>

                <td class="tw-px-4 tw-py-3" v-if="order.billOfLading">{{ order.billOfLading.billOfLadingId }}</td>
                <td class="tw-px-4 tw-py-3" v-else-if="isExportOrder && !isEditingVesselInformation">{{ order.bookingNumber }}</td>
                <td class="tw-px-4 tw-py-3" v-else-if="isExportOrder && isEditingVesselInformation">
                  <InputField id="editBookingNumber" v-model="newBookingNumber"></InputField>
                </td>

                <td class="tw-px-4 tw-py-3">
                  <span v-if="!isEditingVesselInformation">{{ order.vesselName }}</span>
                  <FilterableDropdown
                    v-else
                    taggable
                    push-tag
                    v-model="vesselName"
                    :options="filteredVessels"
                    :components="{ OpenIndicator: null, Deselect: null }"
                  ></FilterableDropdown>
                </td>

                <td class="tw-px-4 tw-py-3">
                  <span v-if="!isEditingVesselInformation">{{ order.vesselEtaString }}</span>
                  <DatePicker
                    v-else
                    class="tw-block"
                    :input-class="'tw-rounded-md tw-border tw-border-slate-200 tw-bg-white/70 tw-px-2 tw-py-1 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-900/10'"
                    format="MM/DD/YYYY"
                    @input="changedETA"
                    v-model="vesselEta"
                  ></DatePicker>
                </td>

                <td class="tw-px-4 tw-py-3">
                  <div v-if="!order.referenceNumber">
                    <div v-if="isEditingReferenceNumber" class="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="text"
                        v-model="editingReferenceNumber"
                        placeholder="Enter reference #"
                        class="tw-w-56 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/80 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-900/10"
                      />
                      <button
                        type="button"
                        class="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-text-slate-700 hover:tw-bg-white"
                        title="Cancel"
                        aria-label="Cancel"
                        @click="cancelReferenceNumberEdit"
                      >
                        <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
                      </button>
                      <button
                        type="button"
                        class="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-transparent tw-bg-slate-900 tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60"
                        title="Save"
                        aria-label="Save"
                        @click="saveReferenceNumber"
                        :disabled="!editingReferenceNumber"
                      >
                        <font-awesome-icon icon="check" class="tw-h-4 tw-w-4" />
                      </button>
                    </div>
                    <button
                      v-else
                      type="button"
                      class="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-text-slate-700 hover:tw-bg-white"
                      @click="startReferenceNumberEdit"
                      title="Add"
                      aria-label="Add"
                    >
                      <font-awesome-icon icon="plus" class="tw-h-4 tw-w-4" />
                    </button>
                  </div>
                  <div v-else-if="!isEditingVesselInformation" class="tw-text-sm tw-text-slate-700">
                    {{ order.referenceNumber }}
                  </div>
                  <InputField v-else id="editReferenceNumber" v-model="newReferenceNumber"></InputField>
                </td>

                <td class="tw-px-4 tw-py-3">
                  <div v-if="!isEditingVesselInformation" class="tw-text-sm tw-text-slate-700">
                    <span v-if="order.billingReferenceNumber">{{ order.billingReferenceNumber }}</span>
                  </div>
                  <InputField v-else id="editReferenceNumber" v-model="newBillingReferenceNumber"></InputField>
                </td>

                <td class="tw-px-4 tw-py-3"></td>
              </tr>
            </tbody>
          </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DispatchOrderQuery from '@/gql/dispatch/query/DispatchOrder.gql';
import EditReferenceNumberMutation from '@/gql/orders/mutations/EditReferenceNumber.gql';
import { pushToast } from '@/lib/toast';

import BillToAddressCard from '../Orders/AddressCards/BillToAddressCard.vue';
import TerminalAddressCard from '../Orders/AddressCards/TerminalAddressCard.vue';
import ReceiverAddressCard from '../Orders/AddressCards/ReceiverAddressCard.vue';

import FilterableDropdown from '../core/FilterableDropdown.vue';
import DatePicker from '@/components/core/DateTime/DatePicker.vue';
import Button from '@/components/core/Button.vue';
import InputField from '@/components/core/Input/InputField.vue';
import { Order } from '@/types';

export default defineComponent({
  props: {
    orderId: {
      type: Number,
      default: -1,
    },
  },

  data(): any {
    return {
      isEditingReferenceNumber: false,
      isEditingVesselInformation: false,
      order: null as Order | null,

      shipLine: undefined,
      steamshipLines: [] as any[],

      newBookingNumber: '',
      vesselName: '',
      vesselEta: null as any,
      filteredVessels: [] as any[],

      editingReferenceNumber: '',
      newReferenceNumber: '',
      newBillingReferenceNumber: '',
    };
  },

  components: {
    BillToAddressCard,
    ReceiverAddressCard,
    TerminalAddressCard,
    FilterableDropdown,
    DatePicker,
    Button,
    InputField,
  },

  methods: {
    refreshOrder() {
      (this as any).$apollo.queries.order.refetch();
    },
    startReferenceNumberEdit() {
      const vm = (this as any);
      vm.isEditingReferenceNumber = true;
      vm.editingReferenceNumber = '';
    },
    cancelReferenceNumberEdit() {
      const vm = (this as any);
      vm.isEditingReferenceNumber = false;
      vm.editingReferenceNumber = '';
    },
    saveReferenceNumber() {
      const vm = (this as any);
      if (!vm.order) return;
      const referenceNumber = String(vm.editingReferenceNumber || '').trim();
      if (!referenceNumber) return;

      vm.$apollo
        .mutate({
          mutation: EditReferenceNumberMutation,
          variables: {
            orderId: Number(vm.order.id),
            referenceNumber,
          },
        })
        .then(() => {
          pushToast('Reference number updated', 'success', 2500);
          vm.isEditingReferenceNumber = false;
          vm.editingReferenceNumber = '';
          vm.refreshOrder();
        })
        .catch((err: any) => {
          console.error(err);
          pushToast('Failed to update reference number', 'error', 3500);
        });
    },
    changedETA(val: any) {
      (this as any).vesselEta = val;
    },
  },

  computed: {
    isExportOrder(): boolean {
      return (this as any).order?.isExport ?? false;
    },
  },

  apollo: {
    order: {
      query: DispatchOrderQuery,
      skip(this: any) {
        return this.orderId === -1;
      },
      variables(this: any) {
        return { id: this.orderId };
      },
      update: (data: any) => data.order,
    },
  },
});
</script>

<style scoped>
/* Ensure the address card components stretch to the same height */
.dispatch-order-card__address :deep(> *) {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
